import apiClient from '@/lib/apiClient'
import { endpoints } from './endPoint'
import { SORT_ORDER } from '@/constants'
import type { SortingState } from '@tanstack/react-table'
import {
  advertisementSortinngColumnsNames,
  type AdvertisementActionsType,
  type UpdateAdvertisementPlacementPayload,
} from '@/constants/sponsor'
import type { Position } from '@/components/sponsorship/DraggableText'

export const fetchSponsorEvents = async (params?: {
  limit?: number
  page?: number
  sort?: SortingState
  isSubEvent?: boolean
  search?: string
}) => {
  try {
    const queryParams = new URLSearchParams()
    if (params?.limit) queryParams.append('limit', params?.limit?.toString())
    if (params?.page) queryParams.append('page', params?.page?.toString())
    if (params?.sort && params?.sort?.length > 0) {
      queryParams.append(
        'order',
        params?.sort?.[0].desc ? SORT_ORDER.DESC : SORT_ORDER.ASC
      )
    }
    if (params?.sort && params?.sort?.length > 0) {
      queryParams.append('orderOn', params?.sort?.[0].id || 'createdAt')
    }
    if (params?.search) {
      queryParams.append('search', params?.search)
    }
    if (params?.isSubEvent !== undefined) {
      queryParams.append('isSubEvent', params.isSubEvent.toString())
    }

    const response = await apiClient.get(
      `${endpoints.sponsor.getSponsorEvent}?${queryParams.toString()}`
    )
    return response.data
  } catch (error) {
    console.error('Error fetching sponsor events:', error)
    throw error
  }
}

export const getSponsorEventDetails = async (eventId: string) => {
  try {
    const response = await apiClient.get(
      endpoints.sponsor.getSponsorshipDetails(eventId)
    )
    return response.data
  } catch (error) {
    console.error('Error fetching sponsorship event details:', error)
  }
}
export const getSponsorShipPlans = async (params: {
  limit?: number
  page?: number
  eventId?: string
  subEventId?: string
}) => {
  const queryParams = new URLSearchParams()
  if (params.limit) queryParams.append('limit', params?.limit?.toString())
  if (params.page) queryParams.append('page', params?.page?.toString())
  if (params.eventId) queryParams.append('eventId', params?.eventId)
  if (params.subEventId) queryParams.append('subEventId', params?.subEventId)

  try {
    const response = await apiClient.get(
      `${endpoints.sponsor.getSponsorShipPlans}?${queryParams.toString()}`
    )
    return response.data
  } catch (error) {
    console.error('Error fetching sponsorShip plans:', error)
    throw error
  }
}

export const getAdPlacementPlans = async (params: {
  limit?: number
  page?: number
}) => {
  const queryParams = new URLSearchParams()
  if (params.limit) queryParams.append('limit', params?.limit?.toString())
  if (params.page) queryParams.append('page', params?.page?.toString())

  try {
    const response = await apiClient.get(
      `${endpoints.sponsor.getAdPlacementPlans}?${queryParams.toString()}`
    )
    return response.data
  } catch (error) {
    console.error('Error fetching ad placement plans:', error)
    throw error
  }
}

export interface SponsorShipPlayload {
  plan: string
}
export const sponsorShipCheckout = async ({
  id,
  payload,
}: {
  id: string
  payload: SponsorShipPlayload
}) => {
  try {
    const resp = await apiClient.put(
      endpoints.sponsor.sponsorShipCheckout(id),
      payload
    )
    return resp.data.data
  } catch (error) {
    console.error('Error while sponsorShip checkout', error)
    throw error
  }
}

export const getAdPlacementTypes = async (params: {
  sponsorshipId: string
}) => {
  try {
    const queryParams = new URLSearchParams()
    if (params.sponsorshipId)
      queryParams.append('sponsorshipId', params?.sponsorshipId)

    const response = await apiClient.get(
      `${endpoints.sponsor.getAdPlacementTypes}?${queryParams.toString()}`
    )
    return response.data
  } catch (error) {
    console.error('Error fetching ad placement types:', error)
    throw error
  }
}

export interface SponsorEventAdvertisementPayload {
  sponsorshipId?: string
  name: string
  description: string
  startDate: Date
  endDate: Date
  link: string
  linkText: string
  sponsorLogo?: string
  eventImage: string
  placement?: string
  sponsorLogoType?: string
  eventImageType: string
  showTitle?: boolean
  showDescription?: boolean
  namePosition?: Position
  descriptionPosition?: Position
  linkPosition?: Position
  sponsorLogoPosition?: Position
}

export const createSponsorEventAdvertisement = async (
  payload: SponsorEventAdvertisementPayload
) => {
  try {
    const resp = await apiClient.post(
      endpoints.sponsor.createSponsorEventAdvertisement,
      payload
    )
    return resp.data.data
  } catch (error) {
    console.error('Error while creating sponsor event advertisement', error)
    throw error
  }
}

export const getAdvertisementList = async (params: {
  limit?: number
  page?: number
  sort?: SortingState
  eventId?: string
  subEventId?: string
  sponsorIds?: string[]
  search?: string
  startDate?: string
  endDate?: string
  status?: string[]
  isAdminRequest?: boolean
}) => {
  try {
    const queryParams = new URLSearchParams()
    if (params.limit) queryParams.append('limit', params?.limit?.toString())
    if (params.page) queryParams.append('page', params?.page?.toString())
    if (params?.sort && params?.sort?.length > 0) {
      queryParams.append(
        'order',
        params?.sort?.[0].desc ? SORT_ORDER.DESC : SORT_ORDER.ASC
      )
    }
    if (params?.sort && params?.sort?.length > 0) {
      let sortID = params?.sort?.[0].id
      if (sortID === advertisementSortinngColumnsNames.duration) {
        sortID = advertisementSortinngColumnsNames.startDate
      }
      queryParams.append(
        'orderOn',
        sortID || advertisementSortinngColumnsNames.startDate
      )
    }
    if (params.eventId) {
      queryParams.append('eventId', params.eventId)
    }
    if (params.subEventId) {
      queryParams.append('subEventId', params.subEventId)
    }
    if (params.sponsorIds && params.sponsorIds.length > 0) {
      params.sponsorIds?.forEach(sponsor => {
        queryParams.append('sponsorIds', sponsor)
      })
    }
    if (params.startDate) {
      queryParams.append('startDate', params.startDate)
    }
    if (params.endDate) {
      queryParams.append('endDate', params.endDate)
    }
    if (params.search) queryParams.append('search', params?.search)
    if (params.status && params.status.length > 0) {
      params?.status?.forEach(status => {
        queryParams.append('statuses', status)
      })
    }
    if (params.isAdminRequest !== undefined) {
      queryParams.append('isAdminRequest', params?.isAdminRequest.toString())
    }

    const response = await apiClient.get(
      `${endpoints.sponsor.getAdvertisementList}?${queryParams.toString()}`
    )
    return response.data
  } catch (error) {
    console.error('Error fetching sponsor event advertisement list:', error)
    throw error
  }
}

export const updateAdvertisementStatus = async ({
  id,
  payload,
}: {
  id: string
  payload: {
    status: AdvertisementActionsType
  }
}) => {
  try {
    const response = await apiClient.put(
      endpoints.sponsor.updateAvertisementStatus(id),
      payload
    )
    return response.data
  } catch (error) {
    console.error('Error updating advertisement status:', error)
    throw error
  }
}

export const getAvailableADDates = async (params: {
  placement: string
  sponsorshipId: string
  advertisementId?: string
}) => {
  try {
    const queryParams = new URLSearchParams()
    if (params.placement) queryParams.append('placement', params?.placement)
    if (params.sponsorshipId)
      queryParams.append('sponsorshipId', params?.sponsorshipId)
    if (params.advertisementId)
      queryParams.append('advertisementId', params?.advertisementId)

    const response = await apiClient.get(
      `${endpoints.sponsor.getAvailableADDates}?${queryParams.toString()}`
    )
    return response.data
  } catch (error) {
    console.error('Error fetching available ad dates:', error)
    throw error
  }
}

export const getAdvertisementDetail = async ({ id }: { id: string }) => {
  try {
    const response = await apiClient.get(
      endpoints.sponsor.getAdvertisementDetail(id)
    )
    return response.data
  } catch (error) {
    console.error('Error updating advertisement detail:', error)
    throw error
  }
}

export const updateAdvertisement = async ({
  id,
  payload,
}: {
  id: string
  payload: SponsorEventAdvertisementPayload
}) => {
  try {
    const response = await apiClient.put(
      endpoints.sponsor.editAdvertisement(id),
      payload
    )
    return response.data
  } catch (error) {
    console.error('Error updating advertisement:', error)
    throw error
  }
}

export const updateAdvertisementAcknowledgement = async ({
  id,
  payload,
}: {
  id: string
  payload: {
    status: AdvertisementActionsType
    reason?: string
  }
}) => {
  try {
    const response = await apiClient.put(
      endpoints.sponsor.updateAdvertisementAcknowledgement(id),
      payload
    )
    return response.data
  } catch (error) {
    console.error('Error updating advertisement acknowledgement:', error)
    throw error
  }
}

export const updateAdvertisementPlacement = async ({
  id,
  payload,
}: {
  id: string
  payload: UpdateAdvertisementPlacementPayload
}) => {
  try {
    const response = await apiClient.put(
      endpoints.sponsor.updateAdvertisementPlacement(id),
      payload
    )
    return response.data
  } catch (error) {
    console.error('Error updating advertisement placement:', error)
    throw error
  }
}

export const updateAdvertisementFrequency = async (
  rotationFrequency: number
) => {
  try {
    const response = await apiClient.put(
      endpoints.sponsor.updateAdvertisementFrequency,
      { rotationFrequency }
    )
    return response.data
  } catch (error) {
    console.error('Error updating advertisement frequency:', error)
    throw error
  }
}

export const getAdvertisementFrequency = async () => {
  try {
    const response = await apiClient.get(
      endpoints.sponsor.getAdvertisementFrequency
    )
    return response.data
  } catch (error) {
    console.error('Error fetching advertisement frequency:', error)
    throw error
  }
}

export const requestEventAdvertisementChanges = async ({
  id,
  payload,
}: {
  id: string
  payload: { subject: string; description: string }
}) => {
  try {
    const response = await apiClient.put(
      endpoints.sponsor.requestEventAdvertisementChanges(id),
      payload
    )
    return response.data
  } catch (error) {
    console.error('Error requesting advertisement changes:', error)
    throw error
  }
}

export const getDemographicsData = async (params?: {
  eventId?: string
  startDate?: string
  endDate?: string
}) => {
  try {
    const queryParams = new URLSearchParams()
    if (params?.eventId) queryParams.append('eventId', params.eventId)
    if (params?.startDate) queryParams.append('startDate', params.startDate)
    if (params?.endDate) queryParams.append('endDate', params.endDate)

    const response = await apiClient.get(
      `${endpoints.sponsor.getDemographicsData}?${queryParams.toString()}`
    )
    return response.data
  } catch (error) {
    console.error('Error fetching demographics data:', error)
    throw error
  }
}

export interface ExportDemographicParams {
  eventId?: string
  startDate?: string
  endDate?: string
}
export const exportDemographicData = async (
  params?: ExportDemographicParams
): Promise<Blob> => {
  try {
    const queryParams = new URLSearchParams()
    if (params?.eventId) queryParams.append('eventId', params.eventId)
    if (params?.startDate) queryParams.append('startDate', params.startDate)
    if (params?.endDate) queryParams.append('endDate', params.endDate)

    const response = await apiClient.get(
      `${endpoints.sponsor.exportDemographics}?${queryParams.toString()}`,
      { responseType: 'blob' }
    )
    return response.data
  } catch (error) {
    console.error('Error exporting demographics data:', error)
    throw error
  }
}

export const getAdvertisementStatistics = async (params: {
  source: 'admin' | 'sponsor'
  month?: string
  year?: string
}) => {
  try {
    const queryParams = new URLSearchParams()
    if (params.source) queryParams.append('source', params.source)
    if (params?.month) queryParams.append('month', params?.month?.toString())
    if (params?.year) queryParams.append('year', params?.year?.toString())

    const response = await apiClient.get(
      `${endpoints.sponsor.getAdvertisementStatistics}?${queryParams.toString()}`
    )
    return response.data
  } catch (error) {
    console.error('Error fetching advertisement statistics data:', error)
    throw error
  }
}

/* Get assigned sponsors to event */

export const getAssignedSponsorsToEvent = async (eventId: string) => {
  try {
    const response = await apiClient.get(
      endpoints.event.getAssignedSponsors(eventId)
    )
    return response.data
  } catch (error) {
    console.error('Error fetching assigned sponsors to event:', error)
    throw error
  }
}

/* Assign sponsors to event */

export const assignSponsorToEvent = async (payload: {
  event: { id: string; sponsors: string[] }
  subEvent: Array<{ id: string; sponsors: string[] }>
}) => {
  try {
    const response = await apiClient.put(
      endpoints.event.assignSponsorToEvent,
      payload
    )
    return response.data
  } catch (error) {
    console.error('Error assigning sponsors to event:', error)
    throw error
  }
}
